import { SummaryType } from '@ls/grouping-service';
import { useDispatch, useSelector } from 'react-redux';
import { DELETE_DEFINITION } from './actions';
import { selectDefinitions, selectFetchingData, selectFetchingBetaSbsData, } from 'state/betas/selectors';
import CalcType from 'state/Constants/CalcType';
import SelectionState from 'state/selections/SelectionState';
import React from 'react';
import { useBetaDecodesQuery as useBetaDecodesQuery_ } from '@ls/entity-mapping-service';
import { selectSideBySideValue } from "state/selections/selectors";

export const useBetaDecodesQuery = () => {
  const query = useBetaDecodesQuery_();
  return React.useMemo(() => {
    const newQuery = { ...query };
    if (newQuery.data) {
      const betas = query.data;
      const betasByBetaId = betas.reduce((map, beta) => {
        map[beta.betaId] = beta;
        return map;
      }, {});

      const betaOptions = betas.map(({ betaId, betaName }) => ({ label: `${betaName} (${betaId})`, value: betaId }));
      const betaOptionsByBetaId = betaOptions.reduce((map, betaOption) => {
        map[betaOption.value] = betaOption;
        return map;
      }, {});

      newQuery.data = {
        betas,
        betasByBetaId,
        betaOptions,
        betaOptionsByBetaId,
      };
    }
    return newQuery
  }, [query]);
};

export const useColumnDefs = () => {
  const definitions = useSelector(selectDefinitions(SelectionState.COMMITTED));
  const fetchingAbsData = useSelector(selectFetchingData(SummaryType.ABSOLUTE));
  const fetchingRelData = useSelector(selectFetchingData(SummaryType.RELATIVE));
  const sideBySideValue = useSelector(
    selectSideBySideValue(SelectionState.COMMITTED)
  );
  const fetchingAbsBetaSbsData = useSelector(
    selectFetchingBetaSbsData(SummaryType.ABSOLUTE)
  );
  const fetchingRelBetaSbsData = useSelector(
    selectFetchingBetaSbsData(SummaryType.RELATIVE)
  );

  const { data } = useBetaDecodesQuery();
  const betasByBetaId = data?.betasByBetaId;
  const dispatch = useDispatch();
  return React.useMemo(() => {
    return definitions.reduce((columnDefs, definition) => {
      const { betaId, calcType, covarianceType, key } = definition;
      const betaEntity = betasByBetaId?.[betaId];
      const baseHeaderName = `${betaEntity?.betaName ?? ''} (${betaId}) - ${covarianceType === 'HIST' ? 'HIST' : 'REG'}`;
      const columnDef = {
        field: key,
        colId: key,
        X_onDelete: () => dispatch({ type: DELETE_DEFINITION, payload: definition }),
      }
      switch (calcType) {
        case CalcType.Account:
          columnDef.headerName = `${baseHeaderName}\nAcc`;
          columnDef.loading = fetchingAbsData;
          break;
        case CalcType.Relative:
          columnDef.headerName = `${baseHeaderName}\nRel`;
          columnDef.loading = fetchingRelData;
          break;
        case CalcType.Benchmark:
          columnDef.headerName = `${baseHeaderName}\nBench`;
          columnDef.loading = fetchingAbsData && fetchingRelData;
          break;
        default:
          throw Error('Unknown calc type: ' + calcType);
      }

      columnDefs.push(columnDef);

      if (sideBySideValue) {
        const sbsColDef = { ...columnDef };
        sbsColDef.colId += " - scenario";
        sbsColDef.field += " - scenario";
        sbsColDef.headerName += " - scenario";
        switch (calcType) {
          case CalcType.Account:

            sbsColDef.loading = fetchingAbsBetaSbsData;
            break;
          case CalcType.Relative:

            columnDef.loading = fetchingRelBetaSbsData;
            break;
          case CalcType.Benchmark:
            sbsColDef.loading = fetchingAbsBetaSbsData && fetchingRelBetaSbsData;
            break;
          default:
            throw Error('Unknown calc type: ' + calcType);
        }
        sbsColDef.X_sbs = true;
        sbsColDef.X_originalColId = key;
        sbsColDef.X_backgroundColor = "#fffb95";
        columnDefs.push(sbsColDef);
      }
      return columnDefs;
    }, []);
  }, [dispatch, betasByBetaId, definitions, fetchingAbsData, fetchingRelData, sideBySideValue, fetchingAbsBetaSbsData, fetchingRelBetaSbsData]);

}